import axios from "axios";
import BoxComponent from "Common/Components/BoxComponent/BoxComponent";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface ReportData {
  current_contractor_remaining_balance: number;
  farmers_commitment_count: number;
  total_delivered_tonnage: number;
  total_farmers_debt: number;
  total_farmers_receivable: number;
  farmers_remaining_settlement: number;
  contractor_fee: number;
  contractor_seed_profit: number;
  contractor_pesticide_profit: number;
  overall_contractor_status: number;
  report_generated_at: string;
  crop_year_name: string;
}

function MainFullReport() {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const cropID = Number(localStorage.getItem("crop_year_id"));

        const response = await axios.post<ReportData>(
          `https://api.havirkesht.ir/report-full/?crop_year_id=${cropID}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("app_atkey")}`,
            },
          }
        );
        setReportData(response.data);
      } catch (err) {
        toast.error("خطا در دریافت گزارش");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const formatNumber = (num: number | undefined) => {
    if (num === undefined) return "-";
    const integerNum = Math.trunc(num); // یا Math.floor(num)
    return new Intl.NumberFormat("fa-IR").format(integerNum);
  };

  const dashboardItems = [
    {
      id: 1,
      title: "مانده فعلی در حساب پیمانکار",
      value: reportData?.current_contractor_remaining_balance,
      rawValue: reportData?.current_contractor_remaining_balance, // مقدار خام برای محاسبات
      colorProp: reportData?.current_contractor_remaining_balance
        ? reportData.current_contractor_remaining_balance > 0
          ? "text-green-600 dark:text-green-400"
          : reportData.current_contractor_remaining_balance < 0
          ? "text-red-600 dark:text-red-400"
          : "text-gray-800 dark:text-gray-100"
        : "text-gray-800 dark:text-gray-100",
      formattedValue: formatNumber(
        reportData?.current_contractor_remaining_balance
      ),
    },
    {
      id: 2,
      title: "تعداد قرارداد کشاورزان",
      value: formatNumber(reportData?.farmers_commitment_count),
      colorProp: "text-gray-800 dark:text-gray-100",
    },
    {
      id: 3,
      title: "کل تناژ تحویلی کشاورزان",
      value: reportData?.total_delivered_tonnage
        ? `${formatNumber(reportData.total_delivered_tonnage)} تن`
        : "-",
      colorProp: "text-gray-800 dark:text-gray-100",
    },
    {
      id: 4,
      title: "جمع بدهی به کشاورزان",
      value: formatNumber(reportData?.total_farmers_debt),
      colorProp: "text-red-600 dark:text-red-400",
    },
    {
      id: 5,
      title: "جمع طلب از کشاورزان",
      value: formatNumber(reportData?.total_farmers_receivable),
      colorProp: "text-green-500 dark:text-green-300",
    },
    {
      id: 6,
      title: "مانده تا تسویه کشاورزان",
      value: reportData?.farmers_remaining_settlement,
      rawValue: reportData?.farmers_remaining_settlement,
      colorProp: reportData?.farmers_remaining_settlement
        ? reportData.farmers_remaining_settlement > 0
          ? "text-green-600 dark:text-green-400"
          : reportData.farmers_remaining_settlement < 0
          ? "text-red-600 dark:text-red-400"
          : "text-gray-800 dark:text-gray-100"
        : "text-gray-800 dark:text-gray-100",
      formattedValue: formatNumber(reportData?.farmers_remaining_settlement),
    },
    {
      id: 7,
      title: "کارمزد پیمانکار (یک درصد)",
      value: reportData?.contractor_fee,
      rawValue: reportData?.contractor_fee,
      colorProp: reportData?.contractor_fee
        ? reportData.contractor_fee > 0
          ? "text-green-600 dark:text-green-400"
          : reportData.contractor_fee < 0
          ? "text-red-600 dark:text-red-400"
          : "text-gray-800 dark:text-gray-100"
        : "text-gray-800 dark:text-gray-100",
      formattedValue: formatNumber(reportData?.contractor_fee),
    },
    {
      id: 8,
      title: "سود پیمانکار از بذر",
      value: formatNumber(reportData?.contractor_seed_profit),
      colorProp: "text-green-500 dark:text-gray-100",
    },
    {
      id: 9,
      title: "سود پیمانکار از سم",
      value: formatNumber(reportData?.contractor_pesticide_profit),
      colorProp: "text-green-500 dark:text-gray-100",
    },
    {
      id: 10,
      title: "وضعیت کلی پیمانکار",
      value:
        reportData?.overall_contractor_status !== undefined
          ? `${formatNumber(reportData.overall_contractor_status)} `
          : "-",
      colorProp: "text-gray-800 dark:text-gray-100",
    },
  ];

  return (
    <section className="bg-white dark:bg-transparent flex justify-start flex-col gap-4 items-center w-full p-1 transition-colors duration-200">
      <div className="rounded-md w-full flex flex-col justify-start items-center p-2 dark:bg-transparent">
        {loading ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>در حال دریافت اطلاعات...</p>
          </div>
        ) : (
          <>
            <div className="w-full flex justify-center items-center gap-3 mb-4">
              <div className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                <span className="dark:text-gray-200">
                  سال زراعی: {reportData?.crop_year_name || "-"}
                </span>
              </div>
            </div>

            <div className="w-full h-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              {dashboardItems.map((data) => (
                <BoxComponent
                  key={data.id}
                  title={data.title}
                  id={data.id}
                  value={data.value}
                  colorProp={data.colorProp}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default MainFullReport;
